import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, Ban, Clock, Plus, Trash2 } from "lucide-react";

// Month calendar for a doctor's personal time. The doctor blocks whole days or
// time ranges; reception sees exactly the same calendar and every availability
// surface (follow-up slots, public booking) stops offering the blocked times
// immediately. Blocks travel with the doctor across all their clinics; the
// bookings listed are this hospital's only.
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function ym(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function iso(y, m, day) { return `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`; }

export default function DoctorCalendar() {
  const { user } = useAuth();
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role];
  const isPureDoctor = roles.includes("doctor") &&
    !roles.some((r) => ["admin", "hospital_admin", "reception"].includes(r));

  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState(isPureDoctor ? user?.id : "");
  const [cursor, setCursor] = useState(() => new Date());
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dayOpen, setDayOpen] = useState("");      // ISO date being edited
  const [blockStart, setBlockStart] = useState("12:00");
  const [blockEnd, setBlockEnd] = useState("13:00");
  const [blockLabel, setBlockLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/doctors").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setDoctors(list);
      if (!doctorId && list.length) setDoctorId(isPureDoctor ? user?.id : list[0].id);
    }).catch(() => setDoctors([]));
    // eslint-disable-next-line
  }, []);

  const month = ym(cursor);

  const load = async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/doctors/${doctorId}/calendar`, { params: { month } });
      setCal(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load calendar");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [doctorId, month]);

  const grid = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(iso(y, m, d));
    return cells;
  }, [cursor]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const blockedSet = new Set(cal?.blocked_dates || []);
  const activeDays = new Set(cal?.active_days || []);
  const blocksFor = (d) => (cal?.time_blocks || []).filter((b) => b.date === d);
  const bookingsFor = (d) => {
    const day = cal?.days?.[d];
    if (!day) return [];
    return [
      ...day.reservations.map((r) => ({ ...r, kind: "Reservation" })),
      ...day.followups.map((f) => ({ ...f, kind: "Follow-up" })),
    ].sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99"));
  };
  const isDayOff = (d) => {
    const dow = DOW[new Date(d + "T00:00:00").getDay()];
    return !activeDays.has(dow);
  };

  // ---- Block edits: always PUT the FULL updated list for the field ---------
  const putSchedule = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/doctor-schedules/${doctorId}`, patch);
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const toggleDayBlocked = async (d) => {
    const next = blockedSet.has(d)
      ? (cal.blocked_dates || []).filter((x) => x !== d)
      : [...(cal.blocked_dates || []), d];
    const n = bookingsFor(d).length;
    if (!blockedSet.has(d) && n > 0 &&
        !window.confirm(`This day already has ${n} booking(s). Block it anyway?\n\nExisting bookings are NOT cancelled — reception should rebook them.`)) return;
    await putSchedule({ blocked_dates: next });
    toast.success(blockedSet.has(d) ? "Day unblocked" : "Day blocked");
  };

  const addTimeBlock = async (d) => {
    if (!blockStart || !blockEnd || blockStart >= blockEnd) {
      toast.error("Start must be before end");
      return;
    }
    await putSchedule({
      time_blocks: [...(cal.time_blocks || []),
        { date: d, start: blockStart, end: blockEnd, label: blockLabel.trim() }],
    });
    setBlockLabel("");
    toast.success(`Blocked ${blockStart}–${blockEnd}`);
  };

  const removeTimeBlock = async (b) => {
    await putSchedule({
      time_blocks: (cal.time_blocks || []).filter(
        (x) => !(x.date === b.date && x.start === b.start && x.end === b.end)),
    });
  };

  const doctorName = doctors.find((d) => d.id === doctorId)?.name || "";

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="doctor-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: "Work Sans" }}>
            <CalendarDays size={26} className="text-blue-700" /> Doctor Calendar
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Block days or hours for personal time — blocked slots disappear from
            follow-up scheduling and online booking immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isPureDoctor ? (
            <select
              value={doctorId}
              onChange={(e) => setDoctorId(e.target.value)}
              className="h-10 rounded-md border border-slate-300 px-3 text-sm"
              data-testid="calendar-doctor-select"
            >
              {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          ) : (
            <span className="text-sm font-medium text-slate-700">{doctorName}</span>
          )}
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev">
            <ChevronLeft size={16} />
          </Button>
          <span className="font-semibold text-slate-900 w-40 text-center" data-testid="cal-month">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </span>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next">
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW.map((d) => (
            <div key={d} className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 py-1">{d}</div>
          ))}
        </div>
        {loading && !cal ? (
          <div className="p-10 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {grid.map((d, i) => d === null ? <div key={`e${i}`} /> : (
              <button
                key={d}
                onClick={() => setDayOpen(d)}
                className={
                  "min-h-[84px] rounded-lg border p-1.5 text-left align-top transition hover:border-blue-400 " +
                  (blockedSet.has(d) ? "bg-red-50 border-red-200 "
                    : isDayOff(d) ? "bg-slate-50 border-slate-200 opacity-60 "
                    : "bg-white border-slate-200 ") +
                  (d === todayIso ? "ring-2 ring-blue-400 " : "")
                }
                data-testid={`cal-day-${d}`}
              >
                <div className="text-xs font-bold text-slate-700">{Number(d.slice(8))}</div>
                {blockedSet.has(d) ? (
                  <div className="mt-1 text-[10px] font-bold text-red-700 flex items-center gap-1"><Ban size={10} /> Blocked</div>
                ) : null}
                {blocksFor(d).map((b, j) => (
                  <div key={j} className="mt-0.5 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 truncate">
                    {b.start}–{b.end}{b.label ? ` ${b.label}` : ""}
                  </div>
                ))}
                {bookingsFor(d).length ? (
                  <div className="mt-0.5 text-[10px] px-1 py-0.5 rounded bg-blue-100 text-blue-800 inline-block">
                    {bookingsFor(d).length} booked
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!dayOpen} onOpenChange={(o) => !o && setDayOpen("")}>
        <DialogContent className="max-w-lg" data-testid="day-dialog">
          <DialogHeader>
            <DialogTitle>{dayOpen}</DialogTitle>
          </DialogHeader>
          {dayOpen ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-slate-50">
                <div>
                  <div className="text-sm font-medium text-slate-900">Entire day</div>
                  <div className="text-xs text-slate-500">
                    {blockedSet.has(dayOpen) ? "Blocked — no bookings can be made" : "Open for bookings"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={blockedSet.has(dayOpen) ? "outline" : "destructive"}
                  onClick={() => toggleDayBlocked(dayOpen)}
                  disabled={saving}
                  data-testid="btn-toggle-day-block"
                >
                  <Ban size={14} className="mr-1" />
                  {blockedSet.has(dayOpen) ? "Unblock day" : "Block whole day"}
                </Button>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  <Clock size={12} className="inline mr-1" /> Personal time blocks
                </div>
                {blocksFor(dayOpen).length === 0 ? (
                  <div className="text-sm text-slate-400">None on this day.</div>
                ) : blocksFor(dayOpen).map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100" data-testid={`time-block-${i}`}>
                    <span className="text-sm text-slate-800">
                      {b.start}–{b.end}{b.label ? <span className="text-slate-500"> · {b.label}</span> : null}
                    </span>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeTimeBlock(b)} disabled={saving}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-4 gap-2 mt-3">
                  <Input type="time" value={blockStart} onChange={(e) => setBlockStart(e.target.value)} className="h-9" data-testid="block-start" />
                  <Input type="time" value={blockEnd} onChange={(e) => setBlockEnd(e.target.value)} className="h-9" data-testid="block-end" />
                  <Input placeholder="Label" value={blockLabel} onChange={(e) => setBlockLabel(e.target.value)} className="h-9" />
                  <Button size="sm" onClick={() => addTimeBlock(dayOpen)} disabled={saving} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="btn-add-block">
                    <Plus size={14} className="mr-1" /> Block
                  </Button>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Bookings this day</div>
                {bookingsFor(dayOpen).length === 0 ? (
                  <div className="text-sm text-slate-400">No bookings.</div>
                ) : (
                  <div className="max-h-40 overflow-auto divide-y divide-slate-100">
                    {bookingsFor(dayOpen).map((b, i) => (
                      <div key={i} className="py-1.5 text-sm flex items-center gap-2">
                        <span className="mono text-xs text-slate-500 w-12">{b.time || "—"}</span>
                        <span className="text-slate-900 truncate flex-1">{b.patient_name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{b.kind}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDayOpen("")}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
